import React, { useEffect, useRef, useState } from "react";
import { ChevronUp, ChevronDown, RotateCcw, Search, X } from "lucide-react";
import { ViewSortDirection } from "../hooks/useTableViewSort";

interface TableViewToolbarProps {
  // Search (useTableSearchHighlight)
  searchInput: string;
  setSearchInput: (val: string) => void;
  searchTerm: string;
  handleSearch: () => void;
  clearSearch: () => void;
  matchCount: number;
  activeIndex: number;
  next: () => void;
  prev: () => void;
  // Sort (useTableViewSort)
  sortField?: string | null;
  sortDirection?: ViewSortDirection;
  isSorted: boolean;
  resetSort: () => void;
  t: (key: string, opts?: any) => string;
  /** Forwarded to NestedTableSearchBar's optional `label` prop — overrides its default
   * "Search Items" text for tables that search something else (machines, procedures). */
  searchLabel?: string;
  /** Дејства прикажани лево од пребарувањето (пр. копирај план во измерено). */
  leadingActions?: React.ReactNode;
}

/**
 * Compact toolbar for OS stage tables. Mirrors the material document spreadsheet toolbar:
 * icon-first search with a sliding input, without save/upload/download actions.
 */
const TableViewToolbar: React.FC<TableViewToolbarProps> = ({
  searchInput,
  setSearchInput,
  searchTerm,
  handleSearch,
  clearSearch,
  matchCount,
  activeIndex,
  next,
  prev,
  isSorted,
  resetSort,
  t,
  searchLabel,
  leadingActions,
}) => {
  const searchActive = searchTerm.trim() !== "";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isSearchOpen) inputRef.current?.focus();
  }, [isSearchOpen]);

  const resolvedPlaceholder = searchLabel ?? t("search.searchItems");

  return (
    <div
      className="flex items-center gap-2"
      onKeyDown={(e) => {
        // Esc closes the sliding search and clears the current in-table highlight.
        if (e.key === "Escape") {
          e.stopPropagation();
          setIsSearchOpen(false);
          clearSearch();
        }
      }}
    >
      {leadingActions}
      <button
        type="button"
        onClick={() => setIsSearchOpen((open) => !open)}
        className="group relative flex items-center justify-center p-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:opacity-90 focus:outline-none focus:ring-2 bg-white"
        style={{ border: "2px solid var(--color-primary)" }}
        title={resolvedPlaceholder}
      >
        <Search size={14} strokeWidth={2.5} style={{ color: "var(--color-primary)" }} />
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isSearchOpen ? "max-w-md opacity-100 ml-1" : "max-w-0 opacity-0 ml-0"
        }`}
      >
        <div className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 shadow-lg border border-gray-200 whitespace-nowrap">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setIsSearchOpen(false);
                clearSearch();
              }
            }}
            placeholder={resolvedPlaceholder}
            className="w-48 flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none"
          />
          {(searchInput || searchTerm) && (
            <button
              type="button"
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title={t("common.clear") || "Clear"}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {searchActive && (
        <div className="flex items-center gap-1 rounded-lg bg-white/15 px-2 py-1 text-xs text-white/90 whitespace-nowrap">
          {matchCount > 0 ? (
            <>
              <span>{t("tableTools.matchCounter", { current: activeIndex + 1, total: matchCount })}</span>
              <button
                type="button"
                onClick={prev}
                className="p-1 rounded hover:bg-white/10 hover:text-white transition-colors"
                title={t("tableTools.prevMatch")}
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={next}
                className="p-1 rounded hover:bg-white/10 hover:text-white transition-colors"
                title={t("tableTools.nextMatch")}
              >
                <ChevronDown size={14} />
              </button>
            </>
          ) : (
            <span>{t("tableTools.noMatches")}</span>
          )}
        </div>
      )}
      {isSorted && (
        <button
          type="button"
          onClick={resetSort}
          className="group relative flex items-center justify-center p-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:opacity-90 focus:outline-none focus:ring-2 bg-white"
          style={{ border: "2px solid var(--color-primary)" }}
          title={t("tableTools.naturalOrder")}
        >
          <RotateCcw size={14} strokeWidth={2.5} style={{ color: "var(--color-primary)" }} />
        </button>
      )}
    </div>
  );
};

export default TableViewToolbar;
